// src/context/TenantContext.js
//
// Phase 7 — Active tenant/site React context with membership awareness.
//
// Responsibilities:
//   - Mirror the framework-agnostic activeTenant store (so path helpers resolve).
//   - Load the signed-in user's membership and expose what they can access.
//   - Offer GUARDED switching: a user can only switch to a tenant/site they're
//     allowed to (owners can switch anywhere).
//   - Correct the active tenant/site to a valid one once membership loads.
//
// Behavior is still safe with the feature flag off: data path resolution does
// not depend on membership, so existing pages keep working.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import {
  getActiveTenant,
  setActiveTenantId as storeSetTenant,
  setActiveSiteId as storeSetSite,
  setActiveTenantSite as storeSetBoth,
  subscribeActiveTenant,
  DEFAULT_TENANT_ID,
  DEFAULT_SITE_ID,
} from "../services/activeTenant";
import {
  loadMembership,
  deriveAccess,
  listTenants,
  listSites,
} from "../services/membershipService";

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const [{ tenantId, siteId }, setLocal] = useState(getActiveTenant);
  const [user, setUser] = useState(() => auth.currentUser);
  const [access, setAccess] = useState(() => deriveAccess(null));
  const [membershipLoading, setMembershipLoading] = useState(true);

  // Metadata for dropdown labels.
  const [tenantsMeta, setTenantsMeta] = useState([]); // [{id, displayName,...}]
  const [sitesMeta, setSitesMeta] = useState([]); // sites of the active tenant

  // Keep React state in sync with the store (switches from anywhere).
  useEffect(() => subscribeActiveTenant(setLocal), []);

  // Track the signed-in user.
  useEffect(() => onAuthStateChanged(auth, setUser), []);

  const refreshMembership = useCallback(async () => {
    if (!user?.email) {
      setAccess(deriveAccess(null));
      setMembershipLoading(false);
      return;
    }
    setMembershipLoading(true);
    try {
      const normalized = await loadMembership(user.email);
      const derived = deriveAccess(normalized);
      setAccess(derived);

      // Correct the active context if the stored one isn't accessible.
      const cur = getActiveTenant();
      if (!derived.canAccessSite(cur.tenantId, cur.siteId) && derived.primary) {
        storeSetBoth(
          derived.primary.tenantId,
          derived.primary.siteId || cur.siteId
        );
      }
    } catch (e) {
      console.error("Failed to load membership:", e);
      setAccess(deriveAccess(null));
    } finally {
      setMembershipLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refreshMembership();
  }, [refreshMembership]);

  // Load the tenant list (owners see all; others see their allowed tenants).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (access.isOwner) {
          const all = await listTenants();
          if (!cancelled) setTenantsMeta(all);
        } else {
          // Non-owners: show only tenants they belong to (fall back to ids).
          const ids = access.allowedTenantIds.length
            ? access.allowedTenantIds
            : [tenantId];
          if (!cancelled)
            setTenantsMeta(ids.map((id) => ({ id, displayName: id })));
        }
      } catch (e) {
        if (!cancelled) setTenantsMeta([{ id: tenantId, displayName: tenantId }]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [access, tenantId]);

  // Load sites of the active tenant for the switcher.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const all = await listSites(tenantId);
        if (cancelled) return;
        if (access.isOwner || access.isTenantAdmin(tenantId)) {
          setSitesMeta(all);
        } else {
          const allowed = new Set(access.sitesForTenant(tenantId));
          setSitesMeta(all.filter((s) => allowed.has(s.id)));
        }
      } catch (e) {
        if (!cancelled) setSitesMeta([{ id: siteId, name: siteId }]);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, access]);

  // --- Guarded switching ----------------------------------------------------
  const switchSite = useCallback(
    (nextSiteId) => {
      if (!nextSiteId) return false;
      if (access.isOwner || access.canAccessSite(tenantId, nextSiteId)) {
        storeSetSite(nextSiteId);
        return true;
      }
      return false;
    },
    [access, tenantId]
  );

  const switchTenant = useCallback(
    (nextTenantId) => {
      if (!nextTenantId) return false;
      const allowed =
        access.isOwner || access.allowedTenantIds.includes(nextTenantId);
      if (!allowed) return false;
      // Land on the first accessible site in the new tenant.
      const sites = access.isOwner ? [] : access.sitesForTenant(nextTenantId);
      const nextSite = sites[0] || null;
      storeSetBoth(nextTenantId, nextSite || getActiveTenant().siteId);
      return true;
    },
    [access]
  );

  const roleForActiveSite = access.roleFor(tenantId, siteId);

  // Whether to show switcher controls at all.
  const canSwitchSite =
    access.isOwner ||
    access.isTenantAdmin(tenantId) ||
    access.sitesForTenant(tenantId).length > 1;
  const canSwitchTenant =
    access.isOwner || access.allowedTenantIds.length > 1;

  const value = useMemo(
    () => ({
      tenantId,
      siteId,
      user,
      access,
      isOwner: access.isOwner,
      isTenantAdmin: access.isTenantAdmin,
      roleForActiveSite,
      membershipLoading,
      tenantsMeta,
      sitesMeta,
      canSwitchSite,
      canSwitchTenant,
      switchSite,
      switchTenant,
      refreshMembership,
      // Low-level (kept for compatibility / programmatic use).
      setTenantId: storeSetTenant,
      setSiteId: storeSetSite,
      setActive: storeSetBoth,
    }),
    [
      tenantId,
      siteId,
      user,
      access,
      roleForActiveSite,
      membershipLoading,
      tenantsMeta,
      sitesMeta,
      canSwitchSite,
      canSwitchTenant,
      switchSite,
      switchTenant,
      refreshMembership,
    ]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant must be used within a <TenantProvider>.");
  }
  return ctx;
}

export { DEFAULT_TENANT_ID, DEFAULT_SITE_ID };
